"use client";

import { type Control, Controller } from "react-hook-form";

import { AuthFormFooter } from "@/app/(discover)/components/auth/auth-form-footer";
import { AuthMethodPanel } from "@/components/auth/auth-method-panel";
import { formMode } from "@/components/auth/form-mode";
import GoogleSignInButtonContainer from "@/components/auth/google-sign-in-button.container";
import type { LoginFormData } from "@/components/auth/login-form.view";
import { SharedDeviceChoice } from "@/components/auth/shared-device-choice";
import {
  AUTH_AUDIENCES,
  type AuthAudience,
} from "@/lib/constants/auth-audiences";
import { authTestIds } from "@/lib/data-test-ids/components.data-test-ids";

interface GoogleSignInStepViewProps {
  mode: formMode;
  audience: AuthAudience;
  isLoading: boolean;
  errorMessage?: string;
  onBack: () => void;
  onGoogleError: (errorMessage: string) => void;
  onGoogleSignInSuccess: () => Promise<void>;
  control: Control<LoginFormData>;
  footer?: React.ReactNode;
}

const FORM_MODE_CONFIG = {
  [formMode.signin]: {
    heading: "Sign in with Google",
    helperText: "Use your Google account to sign in. No password needed.",
  },
  [formMode.signup]: {
    heading: "Sign up with Google",
    helperText:
      "Use a personal Google account, you keep it if you change schools.",
  },
} as const;

const DISTRICT_STAFF_COPY = {
  ...FORM_MODE_CONFIG[formMode.signin],
  helperText: "Use your work Google account to sign in. No password needed.",
} as const;

export default function GoogleSignInStepView({
  mode,
  audience,
  isLoading,
  errorMessage,
  onBack,
  onGoogleError,
  onGoogleSignInSuccess,
  control,
  footer,
}: GoogleSignInStepViewProps) {
  const isDistrictStaff = audience === AUTH_AUDIENCES.DISTRICT_STAFF;
  const modeConfig = isDistrictStaff
    ? DISTRICT_STAFF_COPY
    : FORM_MODE_CONFIG[mode];

  return (
    <AuthMethodPanel
      heading={modeConfig.heading}
      helperText={modeConfig.helperText}
      isLoading={isLoading}
      onBack={onBack}
      footer={
        !isDistrictStaff && (
          <AuthFormFooter showLegalLinks>{footer}</AuthFormFooter>
        )
      }
    >
      <div className="space-y-2">
        <GoogleSignInButtonContainer
          onSignInSuccess={onGoogleSignInSuccess}
          onError={onGoogleError}
          audience={audience}
          mode={mode}
        />
        {errorMessage && (
          <p
            className="text-destructive text-xs text-center"
            data-testid={authTestIds.GOOGLE_STEP_ERROR}
          >
            {errorMessage}
          </p>
        )}
      </div>
      <Controller
        name="sharedDevice"
        control={control}
        render={({ field }) => (
          <SharedDeviceChoice checked={field.value} onChange={field.onChange} />
        )}
      />
    </AuthMethodPanel>
  );
}
